
import { isSpecialDayToday, formatShortDate } from '@/utils/dateUtils';
import { toast } from '@/hooks/use-toast';
import { SpecialDay, User } from '@/utils/types';

export type NotificationMethod = 'inApp' | 'sms' | 'email';

export interface NotificationPreferences {
  inApp: boolean;
  sms: boolean;
  email: boolean;
}

// Function to get customized message based on special day type
export const getCustomMessageForSpecialDay = (specialDay: string, userName?: string): string => {
  const dayType = specialDay.toLowerCase();
  
  if (dayType.includes('birthday')) {
    return userName 
      ? `It's ${userName}'s birthday today! Send them a special gift to celebrate.` 
      : `It's your friend's birthday today! How about sending a birthday gift?`;
  }
  
  if (dayType.includes('anniversary')) {
    return userName 
      ? `It's ${userName}'s anniversary today! Help them celebrate this special milestone.` 
      : `It's your friend's anniversary today! Send them a gift to mark the occasion.`;
  }
  
  if (dayType.includes('promotion')) {
    return userName 
      ? `${userName} got promoted! Send a gift to congratulate them on this achievement.` 
      : `Your friend got promoted! Send a congratulatory gift for this career milestone.`;
  }
  
  if (dayType.includes('graduation')) {
    return userName 
      ? `${userName} is graduating today! Celebrate their academic achievement with a gift.` 
      : `Your friend is graduating today! Send a gift to celebrate this accomplishment.`;
  }
  
  // Default message
  return userName 
    ? `It's ${userName}'s ${specialDay.toLowerCase()} today! Send them a gift to celebrate.` 
    : `It's your friend's ${specialDay.toLowerCase()} today! Send them a quick gift to celebrate.`;
};

// Function to check if a special day is approaching within the given days
export const isSpecialDayApproaching = (specialDay: { date: string, name: string }, daysAhead: number = 3): boolean => {
  if (!specialDay?.date) return false;
  
  const today = new Date();
  const futureDate = new Date();
  futureDate.setDate(today.getDate() + daysAhead);
  
  // Create date object from specialDay date
  const specialDate = new Date(specialDay.date);
  
  // Check if the special day falls within the next X days
  // First, create a date with current year
  const thisYearDate = new Date(
    today.getFullYear(),
    specialDate.getMonth(),
    specialDate.getDate()
  );
  
  // If the date has already passed this year, check for next year
  if (thisYearDate < today) {
    thisYearDate.setFullYear(today.getFullYear() + 1);
  }
  
  // Return true if the special day falls within the next X days
  return thisYearDate <= futureDate && thisYearDate > today;
};

export const checkForSpecialDays = (
  specialDays: SpecialDay[],
  userNetwork: User[] = [],
  notificationPreferences: NotificationPreferences = { inApp: true, sms: false, email: false }
): { hasSpecialDayToday: boolean, notifications: Array<{ to: User, message: string, method: NotificationMethod }> } => {
  if (!specialDays?.length) {
    return { hasSpecialDayToday: false, notifications: [] };
  }
  
  // Filter to only get special days that are today and enabled
  const todaysSpecialDays = specialDays.filter(
    day => day.enabled && day.date && isSpecialDayToday({ date: day.date, name: day.name })
  );
  
  // Also check for upcoming special days for advance notifications
  const upcomingSpecialDays = specialDays.filter(
    day => day.enabled && day.date && isSpecialDayApproaching({ date: day.date, name: day.name })
  );
  
  if (!todaysSpecialDays.length && !upcomingSpecialDays.length) {
    return { hasSpecialDayToday: false, notifications: [] };
  }
  
  // Generate notifications for network
  const notifications: Array<{ to: User, message: string, method: NotificationMethod }> = [];
  
  // For each special day today, notify people in the network
  todaysSpecialDays.forEach(specialDay => {
    if (userNetwork.length > 0) {
      userNetwork.forEach(contact => {
        const customMessage = getCustomMessageForSpecialDay(specialDay.name, contact.name);
        
        // Check which notification methods are enabled
        if (notificationPreferences.inApp) {
          notifications.push({
            to: contact,
            message: customMessage,
            method: 'inApp'
          });
        }
        
        if (notificationPreferences.sms && contact.phone) {
          notifications.push({
            to: contact,
            message: customMessage,
            method: 'sms'
          });
        }
        
        if (notificationPreferences.email && contact.email) {
          notifications.push({
            to: contact,
            message: customMessage,
            method: 'email'
          });
        }
      });
    }
  });
  
  // For each upcoming special day, create advance notifications
  upcomingSpecialDays.forEach(specialDay => {
    if (userNetwork.length > 0) {
      userNetwork.forEach(contact => {
        const daysUntil = getDaysUntil(specialDay.date);
        const reminderMessage = `Reminder: ${contact.name}'s ${specialDay.name.toLowerCase()} is coming up ${daysUntil}. Plan a gift in advance!`;
        
        // Add advance notifications with the same preferences
        if (notificationPreferences.inApp) {
          notifications.push({
            to: contact,
            message: reminderMessage,
            method: 'inApp'
          });
        }
        
        if (notificationPreferences.sms && contact.phone) {
          notifications.push({
            to: contact,
            message: reminderMessage,
            method: 'sms'
          });
        }
        
        if (notificationPreferences.email && contact.email) {
          notifications.push({
            to: contact,
            message: reminderMessage,
            method: 'email'
          });
        }
      });
    }
  });
  
  return {
    hasSpecialDayToday: todaysSpecialDays.length > 0,
    notifications
  };
};

// Helper function to calculate days until special day (similar to the one in UpcomingSpecialDays)
const getDaysUntil = (dateString: string): string => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const date = new Date(dateString);
  date.setHours(0, 0, 0, 0);
  
  // If the date is in the past for this year, we need to calculate for next year
  if (date < today && date.getMonth() < today.getMonth()) {
    date.setFullYear(today.getFullYear() + 1);
  } else if (date < today) {
    date.setFullYear(today.getFullYear());
  }
  
  const diffTime = Math.abs(date.getTime() - today.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return "today";
  if (diffDays === 1) return "tomorrow";
  return `in ${diffDays} days`;
};

// This function would actually send the notifications
// In a real app, this would connect to a backend service
export const sendNotifications = (
  notifications: Array<{ to: User, message: string, method: NotificationMethod }>
): Promise<boolean> => {
  return new Promise((resolve) => {
    // Mock sending notifications
    console.log('Sending notifications:', notifications);
    
    // In a real app, we would make API calls here
    setTimeout(() => {
      toast({
        title: 'Notifications Sent',
        description: `${notifications.length} notifications sent to your network`
      });
      resolve(true);
    }, 1000);
  });
};

// Check if today is the user's special day and notify the user
export const checkForPersonalSpecialDay = (specialDays: SpecialDay[]): SpecialDay | null => {
  if (!specialDays?.length) return null;
  
  // Check for today's special days
  const todaySpecialDay = specialDays.find(
    day => day.enabled && day.date && isSpecialDayToday({ date: day.date, name: day.name })
  );
  
  if (todaySpecialDay) {
    toast({
      title: `Happy ${todaySpecialDay.name}!`,
      description: 'Your network has been notified. Expect some gifts today!',
      variant: 'default'
    });
    return todaySpecialDay;
  }
  
  // Check for upcoming special days
  const upcomingSpecialDay = specialDays.find(
    day => day.enabled && day.date && isSpecialDayApproaching({ date: day.date, name: day.name })
  );
  
  if (upcomingSpecialDay) {
    const daysUntil = getDaysUntil(upcomingSpecialDay.date);
    toast({
      title: `${upcomingSpecialDay.name} Reminder`,
      description: `Your ${upcomingSpecialDay.name.toLowerCase()} is coming up ${daysUntil}!`,
      variant: 'default'
    });
  }
  
  return null;
};
